import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { getDemoDataForCollection } from '../demoData';
import { db } from '../firebase/config';
import { 
  Globe2, Search, Filter, ArrowRight, ChevronUp, ChevronDown, 
  Download, RefreshCw, Eye, X, ExternalLink, MapPin, Phone, 
  Mail, ShieldCheck, DollarSign, Users, Award, Building2, 
  Briefcase, Laptop, GraduationCap, CheckCircle2, Clock
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Role configuration
const COLLECTIONS_CONFIG = [
  { id: 'students', roleName: 'Student', color: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20', basePath: 'students', icon: GraduationCap },
  { id: 'trainers', roleName: 'Trainer', color: 'bg-blue-500/10 text-blue-400 border border-blue-500/20', basePath: 'trainers', icon: Users },
  { id: 'industries', roleName: 'Industry Partner', color: 'bg-teal-500/10 text-teal-400 border border-teal-500/20', basePath: 'industries', icon: Briefcase },
  { id: 'sales_reps', roleName: 'Sales Rep', color: 'bg-amber-500/10 text-amber-400 border border-amber-500/20', basePath: 'sales-reps', icon: DollarSign },
  { id: 'enterprise_tenants', roleName: 'Tasks and Customer Management (TCM)', color: 'bg-pink-500/10 text-pink-400 border border-pink-500/20', basePath: 'tcm', icon: Laptop },
  { id: 'sponsors', roleName: 'Sponsor', color: 'bg-purple-500/10 text-purple-400 border border-purple-500/20', basePath: 'sponsors', icon: Award },
  { id: 'campuses', roleName: 'Physical Campus', color: 'bg-orange-500/10 text-orange-400 border border-orange-500/20', basePath: 'campuses', icon: Building2 },
  { id: 'partners', roleName: 'Strategic Partner', color: 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20', basePath: 'partners', icon: ShieldCheck },
  { id: 'institutions', roleName: 'Academic Institution', color: 'bg-rose-500/10 text-rose-400 border border-rose-500/20', basePath: 'institutions', icon: GraduationCap },
  { id: 'scholarships', roleName: 'Scholarship Grant', color: 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20', basePath: 'scholarships', icon: Award }
];

// Helper: Safely convert timestamps into millisecond numbers
const getTimestampMs = (val) => {
  if (!val) return 0;
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const ms = new Date(val).getTime();
    return isNaN(ms) ? 0 : ms;
  }
  if (val.seconds) return val.seconds * 1000;
  if (typeof val.toDate === 'function') {
    try { return val.toDate().getTime(); } catch (_) { return 0; }
  }
  return 0;
};

// Helper: Safely format date string
const formatDateSafe = (val) => {
  if (!val) return 'N/A';
  try {
    let d;
    if (typeof val === 'string') d = new Date(val);
    else if (typeof val === 'number') d = new Date(val);
    else if (val.seconds) d = new Date(val.seconds * 1000);
    else if (typeof val.toDate === 'function') d = val.toDate();
    if (d && !isNaN(d.getTime())) {
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }
  } catch (_) {}
  return 'N/A';
};

// Slide-Over Dossier Drawer for any Ecosystem Record
const UniversalDossierPanel = ({ record, onClose, onNavigate }) => {
  const [showRawJson, setShowRawJson] = useState(false);
  if (!record) return null;

  const data = record.raw || {};
  const roleMeta = record.roleMeta;
  const RoleIcon = roleMeta.icon || Globe2;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] transition-opacity animate-in fade-in"
        onClick={onClose}
      />
      <div className="fixed inset-y-0 right-0 w-full md:w-[650px] lg:w-[800px] bg-zinc-950 border-l border-zinc-800 shadow-2xl z-[101] flex flex-col animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="flex-none p-6 border-b border-zinc-800 bg-zinc-900/60">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className={`px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-full flex items-center gap-1.5 ${roleMeta.color}`}>
                  <RoleIcon size={12} /> {roleMeta.roleName}
                </span>
                <span className="text-xs font-mono font-bold text-zinc-400 bg-zinc-900 px-2 py-0.5 rounded border border-zinc-800">
                  {record.id}
                </span>
                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                  String(record.status || '').toLowerCase().includes('active') || 
                  String(record.status || '').toLowerCase().includes('paid') || 
                  String(record.status || '').toLowerCase().includes('approved') || 
                  String(record.status || '').toLowerCase().includes('verified')
                    ? 'bg-emerald-950 text-emerald-400 border border-emerald-800'
                    : 'bg-zinc-800 text-zinc-400'
                }`}>
                  {record.status || 'Active'}
                </span>
              </div>
              <h2 className="text-2xl font-black text-white">{record.name}</h2>
              <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-zinc-400">
                {record.email && record.email !== 'N/A' && (
                  <span className="flex items-center gap-1.5"><Mail size={13} /> {record.email}</span>
                )}
                {record.phone && record.phone !== 'N/A' && (
                  <span className="flex items-center gap-1.5"><Phone size={13} /> {record.phone}</span>
                )}
                {record.location && record.location !== 'N/A' && (
                  <span className="flex items-center gap-1.5"><MapPin size={13} /> {record.location}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => onNavigate(roleMeta.basePath, record.id)}
                className="px-3.5 py-2 bg-[#8B1A2D] hover:bg-[#a61f37] text-white text-xs font-black uppercase tracking-wider rounded-xl transition-all flex items-center gap-1.5 shadow-lg shadow-[#8B1A2D]/20 cursor-pointer"
              >
                <span>Full Ledger</span>
                <ExternalLink size={13} />
              </button>
              <button onClick={onClose} className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl transition-colors cursor-pointer">
                <X size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Key Metric Highlights */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800">
              <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Capacity / Sizing</div>
              <div className="text-sm font-bold text-white mt-1">{record.capacityMetrics || 'Standard Operational'}</div>
            </div>
            <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800">
              <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Legal / Regulatory PIN</div>
              <div className="text-sm font-mono font-bold text-amber-400 mt-1">{record.regulatoryPin || 'N/A'}</div>
            </div>
            <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800">
              <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Settlement / Billing</div>
              <div className="text-sm font-bold text-emerald-400 mt-1">{record.billingRef || 'Standard Account'}</div>
            </div>
            <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800">
              <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Registered Date</div>
              <div className="text-xs font-mono font-bold text-zinc-300 mt-1">{record.formattedDate || 'N/A'}</div>
            </div>
          </div>

          {/* Operational Attributes Panel */}
          <div className="p-6 bg-zinc-900/40 rounded-2xl border border-zinc-800/80 space-y-4">
            <h3 className="text-xs font-black uppercase tracking-wider text-zinc-300 border-b border-zinc-800 pb-2">
              Operational Attributes &amp; Schema Mapping
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              {Object.entries(data).slice(0, 16).map(([key, val]) => {
                if (typeof val === 'object' && val !== null) {
                  return (
                    <div key={key} className="p-3 bg-zinc-900/80 rounded-xl border border-zinc-800/50">
                      <span className="text-[10px] text-zinc-500 font-mono uppercase">{key}</span>
                      <div className="text-zinc-300 font-mono text-[11px] truncate mt-0.5">
                        {JSON.stringify(val)}
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={key} className="p-3 bg-zinc-900/80 rounded-xl border border-zinc-800/50">
                    <span className="text-[10px] text-zinc-500 font-mono uppercase">{key}</span>
                    <div className="text-zinc-200 font-semibold truncate mt-0.5">
                      {String(val || 'N/A')}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Raw JSON Debug Toggle */}
          <div className="border border-zinc-800 rounded-2xl overflow-hidden">
            <button
              onClick={() => setShowRawJson(!showRawJson)}
              className="w-full p-4 bg-zinc-900/80 flex items-center justify-between text-xs font-bold text-zinc-400 hover:text-white transition-colors cursor-pointer"
            >
              <span>Raw Firestore Document Payload</span>
              {showRawJson ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {showRawJson && (
              <pre className="p-4 bg-black text-[10px] font-mono text-emerald-400 overflow-x-auto max-h-60 border-t border-zinc-800">
                {JSON.stringify(data, null, 2)}
              </pre>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default function UniversalRegistry({ showDemo, setShowDemo }) {
  const navigate = useNavigate();
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedRecord, setSelectedRecord] = useState(null);

  // High-fidelity multi-role field extractor
  const extractHighFidelityFields = (id, data = {}, meta) => {
    let displayName = data.name || data.displayName;
    if (!displayName && data.companyLegalName) displayName = data.companyLegalName;
    if (!displayName && data.partnerName) displayName = data.partnerName;
    if (!displayName && data.workspaceName) displayName = data.workspaceName;
    if (!displayName && data.orgName) displayName = data.orgName;
    if (!displayName && data.campusName) displayName = data.campusName;
    if (!displayName && data.repCode) displayName = `Rep: ${data.name || data.repCode}`;
    if (!displayName && (data.firstName || data.lastName)) displayName = `${data.firstName || ''} ${data.lastName || ''}`.trim();
    if (!displayName && data.applicationRef) displayName = `Applicant ${data.applicationRef}`;
    if (!displayName) displayName = `Entity ${String(id).slice(0, 8)}`;

    // Email
    let emailVal = data.email || data.primaryContactEmail || data.primaryAdminEmail || data.contactPersonEmail || data.liaisonEmail || data.vcEmail || 'N/A';
    if (emailVal === 'N/A' && data.subdomain) emailVal = `${data.subdomain}.dynamicindutechno.solutions`;

    // Phone / Mobile
    let phoneVal = data.phone || data.contactPhone || data.primaryPhone || data.mpesaPhone || data.directorPhone || data.technicianPhone || 'N/A';

    // Location
    let locVal = data.primaryTerritory || data.county || data.city || data.town || data.country || 'N/A';
    if (data.physicalAddress && locVal === 'N/A') locVal = data.physicalAddress;

    // Sizing / Operational capacity
    let sizingVal = 'Standard';
    if (meta.id === 'students') {
      sizingVal = data.candidateProgramme || data.track || data.course || 'DIS Scholar';
    } else if (meta.id === 'campuses') {
      sizingVal = `${data.workstations || data.capacity || 50} Workstations • ${data.bandwidthMbps || 100} Mbps`;
    } else if (meta.id === 'enterprise_tenants') {
      sizingVal = `${data.seatsCount || data.seats || 25} Seats • ${data.tier || 'Circle'}`;
    } else if (meta.id === 'trainers') {
      sizingVal = `${data.primarySpecialization || 'Tech Instructor'} • ${data.nexusTier || 'Tier 1'}`;
    } else if (meta.id === 'industries') {
      sizingVal = `${data.internshipSlots || 10} Slots • ${data.techStackPrimary || 'Tech Stack'}`;
    } else if (meta.id === 'sales_reps') {
      sizingVal = `${data.commissionRate || 10}% Comm • ${data.payoutFrequency || 'Weekly'}`;
    } else if (meta.id === 'sponsors') {
      sizingVal = `$${(data.annualGrantCommitmentUSD || 0).toLocaleString()} USD Grant Pool`;
    } else if (meta.id === 'scholarships') {
      sizingVal = `$${(data.financials?.valueUSD || data.totalPoolUSD || 0).toLocaleString()} USD (${data.targetQuota || 25} Scholars)`;
    } else if (meta.id === 'partners') {
      sizingVal = data.partnerType || data.category || 'Strategic Enterprise';
    } else if (meta.id === 'institutions') {
      sizingVal = `${data.studentPopulation || 5000} Enrolled • ${data.revenueSharePct || 30}% RevShare`;
    }

    // Regulatory / Identification PIN
    let pinVal = data.applicationRef || data.nationalId || data.kraPin || data.cr12Number || data.charterNumber || data.accreditationNumber || data.repCode || data.subdomain || id;

    // Billing / Settlement Ref
    let billingVal = data.mpesaReceipt || data.activationRef || data.fee_status || data.paymentStatus || data.payoutMpesa || data.bankAccount || 'Verified';

    // Status
    let statusVal = data.status || data.fee_status || data.paymentStatus || data.approvalStatus || 'Active';

    // Dates & Milliseconds
    const dateRaw = data.addedAt || data.submittedAt || data.createdAt || data.updatedAt || data.approvedAt || null;
    const formattedDate = formatDateSafe(dateRaw);
    const dateMs = getTimestampMs(dateRaw);

    return {
      id: String(id),
      name: String(displayName),
      email: String(emailVal),
      phone: String(phoneVal),
      location: String(locVal),
      capacityMetrics: String(sizingVal),
      regulatoryPin: String(pinVal),
      billingRef: String(billingVal),
      status: String(statusVal),
      addedAt: dateRaw,
      dateMs,
      formattedDate,
      roleMeta: meta,
      raw: data
    };
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Use Promise.allSettled so no single collection failure can abort the others
      const results = await Promise.allSettled(
        COLLECTIONS_CONFIG.map(async (c) => {
          const snap = await getDocs(collection(db, c.id));
          const list = [];
          snap.forEach(docSnap => {
            const data = docSnap.data();
            list.push(extractHighFidelityFields(docSnap.id, data, c));
          });
          return list;
        })
      );

      let mergedData = [];
      results.forEach((res, index) => {
        const meta = COLLECTIONS_CONFIG[index];
        if (res.status === 'fulfilled' && Array.isArray(res.value)) {
          mergedData.push(...res.value);
        } else {
          console.warn(`Collection ${meta.id} query skipped or returned error:`, res.reason);
        }

        // Demo records injection if active
        if (showDemo) {
          const demoItems = getDemoDataForCollection(meta.id) || [];
          demoItems.forEach(demoData => {
            mergedData.push(extractHighFidelityFields(demoData.id || `demo-${Math.random()}`, demoData, meta));
          });
        }
      });

      // Pure numeric sorting by timestamp ms (impossible to throw TypeError)
      mergedData.sort((a, b) => b.dateMs - a.dateMs);

      setAllRecords(mergedData);
    } catch (e) {
      console.error("Error fetching universal registry:", e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAllData();
  }, [showDemo]);

  // Robust Filter Logic
  const filteredRecords = useMemo(() => {
    return allRecords.filter(record => {
      const matchesRole = filterRole === 'all' || record.roleMeta.id === filterRole;
      
      const recordStatus = String(record.status || '').toLowerCase();
      let matchesStatus = true;
      if (filterStatus !== 'all') {
        matchesStatus = recordStatus.includes(filterStatus.toLowerCase());
      }

      const q = searchTerm.trim().toLowerCase();
      const matchesSearch = !q ||
        String(record.name || '').toLowerCase().includes(q) ||
        String(record.email || '').toLowerCase().includes(q) ||
        String(record.phone || '').toLowerCase().includes(q) ||
        String(record.location || '').toLowerCase().includes(q) ||
        String(record.regulatoryPin || '').toLowerCase().includes(q) ||
        String(record.id || '').toLowerCase().includes(q) ||
        String(record.capacityMetrics || '').toLowerCase().includes(q);

      return matchesRole && matchesStatus && matchesSearch;
    });
  }, [allRecords, searchTerm, filterRole, filterStatus]);

  // Master CSV Export across all entities
  const exportMasterCSV = () => {
    const headers = [
      'Role', 'Database ID', 'Display Name', 'Email', 'Phone', 'Location',
      'Capacity / Sizing', 'Regulatory / ID PIN', 'Billing Ref', 'Status', 'Registered Date'
    ];
    const rows = filteredRecords.map(r => [
      `"${r.roleMeta.roleName}"`,
      r.id,
      `"${r.name}"`,
      r.email,
      r.phone,
      `"${r.location}"`,
      `"${r.capacityMetrics}"`,
      `"${r.regulatoryPin}"`,
      `"${r.billingRef}"`,
      r.status,
      r.formattedDate
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `DIS_Universal_Ecosystem_Registry_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Executive Header Banner */}
      <div className="bg-gradient-to-r from-zinc-950 via-zinc-900 to-black border border-zinc-800 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#8B1A2D]/10 blur-3xl rounded-full pointer-events-none -mr-20 -mt-20" />
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <span className="p-2.5 bg-[#8B1A2D]/20 text-[#8B1A2D] border border-[#8B1A2D]/30 rounded-2xl">
                <Globe2 size={22} />
              </span>
              <span className="px-3 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[10px] font-black uppercase tracking-widest rounded-full">
                Ecosystem Master Switchboard • Global Search
              </span>
            </div>
            <h1 className="text-3xl font-black text-white uppercase tracking-tight">
              Universal Ecosystem Registry
            </h1>
            <p className="text-zinc-400 text-xs font-bold max-w-2xl leading-relaxed">
              Global operational index federating all 10 DIS stakeholder directories. Query across students, corporate tenants, academic campuses, trainers, and enterprise sponsors with real-time cross-cutting analytics.
            </p>
          </div>

          <div className="flex items-center gap-4 bg-zinc-900/90 border border-zinc-800 p-4 rounded-2xl">
            <div className="text-right">
              <div className="text-[10px] font-black text-zinc-400 uppercase tracking-wider">Indexed Ecosystem Records</div>
              <div className="text-2xl font-black text-white">
                {allRecords.length} <span className="text-xs text-[#8B1A2D] font-bold">Entities</span>
              </div>
            </div>
            <button
              onClick={exportMasterCSV}
              className="flex items-center gap-2 px-5 py-3 bg-[#8B1A2D] hover:bg-[#a61f37] text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-[#8B1A2D]/20 cursor-pointer"
            >
              <Download size={14} /> Export Global CSV
            </button>
          </div>
        </div>
      </div>

      {/* Spreadsheet Master Table Container */}
      <div className="bg-zinc-950 border border-zinc-800 rounded-3xl p-6 shadow-2xl space-y-4">
        {/* Table Toolbar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-zinc-800 pb-4">
          <div className="flex items-center gap-3">
            <span className="p-2 bg-zinc-900 border border-zinc-800 rounded-xl text-zinc-300">
              <Filter size={16} />
            </span>
            <div>
              <h2 className="text-sm font-black text-white uppercase tracking-wider">
                Showing {filteredRecords.length} of {allRecords.length} Master Entities
              </h2>
              <p className="text-[10px] text-zinc-500">Google Sheets-grade spreadsheet with freeze panes and instant slide-over inspector</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Master Search Input */}
            <div className="relative min-w-[260px]">
              <Search className="absolute left-3 top-2.5 text-zinc-500" size={14} />
              <input
                type="text"
                placeholder="Search across all 10 roles globally..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-800 text-white text-xs rounded-xl pl-9 pr-4 py-2 focus:outline-none focus:border-[#8B1A2D] placeholder-zinc-500"
              />
            </div>

            {/* Role Filter */}
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs rounded-xl px-3 py-2 focus:outline-none focus:border-[#8B1A2D]"
            >
              <option value="all">All 10 Ecosystem Roles</option>
              {COLLECTIONS_CONFIG.map(c => (
                <option key={c.id} value={c.id}>{c.roleName}</option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs rounded-xl px-3 py-2 focus:outline-none focus:border-[#8B1A2D]"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active Only</option>
              <option value="paid">Paid / Approved</option>
              <option value="pending">Pending Only</option>
              <option value="suspended">Suspended</option>
            </select>

            {/* Demo Toggle */}
            <button
              onClick={() => setShowDemo(!showDemo)}
              className={`text-[9px] font-black uppercase tracking-widest px-3 py-2 rounded-xl border transition-all cursor-pointer ${
                showDemo ? 'border-[#8B1A2D] text-white bg-[#8B1A2D]' : 'border-zinc-800 text-zinc-400 bg-zinc-900 hover:text-white'
              }`}
            >
              {showDemo ? 'Hide Demo' : 'Show Demo'}
            </button>

            {/* Refresh */}
            <button
              onClick={fetchAllData}
              className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl transition-colors cursor-pointer"
              title="Refresh Global Index"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Master Table */}
        {loading ? (
          <div className="py-20 text-center text-zinc-500 font-bold text-xs uppercase tracking-widest animate-pulse">
            Querying Multi-Tenant Cloud Firestore Across All 10 Ecosystem Portals...
          </div>
        ) : filteredRecords.length === 0 ? (
          <div className="py-20 text-center text-zinc-500 font-bold text-xs uppercase tracking-widest">
            No entities match your active filters.
          </div>
        ) : (
          <div className="overflow-x-auto border border-zinc-800/80 rounded-2xl">
            <table className="w-full text-left border-collapse min-w-[1700px]">
              <thead>
                <tr className="bg-zinc-900/90 border-b border-zinc-800 text-[9px] font-black uppercase tracking-widest text-zinc-400">
                  {/* Sticky 0: Index */}
                  <th className="py-3 px-3 text-center w-12 sticky left-0 z-30 bg-zinc-900 border-r border-zinc-800">
                    #
                  </th>
                  {/* Sticky 1: Record Name */}
                  <th className="py-3 px-4 min-w-[240px] sticky left-12 z-30 bg-zinc-900 border-r border-zinc-800 shadow-[2px_0_5px_rgba(0,0,0,0.5)]">
                    Entity / Stakeholder Name
                  </th>
                  <th className="py-3 px-4">Ecosystem Role</th>
                  <th className="py-3 px-4">Database Identifier</th>
                  <th className="py-3 px-4">Primary Contact Phone</th>
                  <th className="py-3 px-4">Primary Contact Email</th>
                  <th className="py-3 px-4">Location / Territory</th>
                  <th className="py-3 px-4">Operational Sizing / Capacity</th>
                  <th className="py-3 px-4">Accreditation / Tax PIN</th>
                  <th className="py-3 px-4">Settlement / Billing Ref</th>
                  <th className="py-3 px-4">Onboarded Date</th>
                  <th className="py-3 px-4 text-center">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/60 text-xs font-medium">
                {filteredRecords.map((record, idx) => {
                  const roleMeta = record.roleMeta;
                  const RoleIcon = roleMeta.icon || Globe2;

                  return (
                    <tr 
                      key={`${record.roleMeta.id}-${record.id}-${idx}`}
                      onClick={() => setSelectedRecord(record)}
                      className="hover:bg-zinc-900/60 transition-colors cursor-pointer group"
                    >
                      {/* Sticky 0 */}
                      <td className="py-3.5 px-3 text-center text-zinc-500 font-mono sticky left-0 z-20 bg-zinc-950 group-hover:bg-zinc-900 border-r border-zinc-800/80">
                        {idx + 1}
                      </td>

                      {/* Sticky 1 */}
                      <td className="py-3.5 px-4 sticky left-12 z-20 bg-zinc-950 group-hover:bg-zinc-900 border-r border-zinc-800/80 shadow-[2px_0_5px_rgba(0,0,0,0.5)]">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <div className="font-bold text-white group-hover:text-[#8B1A2D] transition-colors">
                              {record.name}
                            </div>
                            <div className="text-[10px] font-mono text-zinc-500 mt-0.5 truncate max-w-[190px]">
                              {record.id}
                            </div>
                          </div>
                          <Eye size={14} className="text-zinc-600 group-hover:text-amber-400 transition-colors" />
                        </div>
                      </td>

                      {/* Role */}
                      <td className="py-3.5 px-4">
                        <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-full flex items-center gap-1.5 w-fit ${roleMeta.color}`}>
                          <RoleIcon size={12} /> {roleMeta.roleName}
                        </span>
                      </td>

                      {/* ID */}
                      <td className="py-3.5 px-4 font-mono text-zinc-400 text-[11px]">
                        <span className="bg-zinc-900 px-2 py-0.5 rounded border border-zinc-800">
                          {record.id}
                        </span>
                      </td>

                      {/* Phone */}
                      <td className="py-3.5 px-4 font-mono text-zinc-300">
                        {record.phone}
                      </td>

                      {/* Email */}
                      <td className="py-3.5 px-4 text-zinc-400 truncate max-w-[180px]">
                        {record.email}
                      </td>

                      {/* Location */}
                      <td className="py-3.5 px-4 text-zinc-300 truncate max-w-[150px]">
                        {record.location}
                      </td>

                      {/* Sizing / Capacity */}
                      <td className="py-3.5 px-4 text-emerald-400 font-medium truncate max-w-[180px]">
                        {record.capacityMetrics}
                      </td>

                      {/* Regulatory PIN */}
                      <td className="py-3.5 px-4 font-mono text-amber-300 text-[11px]">
                        {record.regulatoryPin}
                      </td>

                      {/* Billing Ref */}
                      <td className="py-3.5 px-4 text-zinc-400 text-[11px] truncate max-w-[140px]">
                        {record.billingRef}
                      </td>

                      {/* Registered Date */}
                      <td className="py-3.5 px-4 font-mono text-zinc-300">
                        {record.formattedDate}
                      </td>

                      {/* Status */}
                      <td className="py-3.5 px-4 text-center">
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                          record.status.toLowerCase().includes('active') || 
                          record.status.toLowerCase().includes('paid') || 
                          record.status.toLowerCase().includes('approved') || 
                          record.status.toLowerCase().includes('verified')
                            ? 'bg-emerald-950/80 text-emerald-400 border border-emerald-800'
                            : 'bg-zinc-800 text-zinc-400'
                        }`}>
                          {record.status || 'Active'}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedRecord(record);
                            }}
                            className="p-1.5 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-lg transition-colors cursor-pointer"
                            title="Inspect Master Dossier"
                          >
                            <Eye size={13} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/${roleMeta.basePath}/${record.id}`);
                            }}
                            className="p-1.5 text-zinc-400 hover:text-[#8B1A2D] bg-zinc-900 border border-zinc-800 rounded-lg transition-colors cursor-pointer"
                            title="Navigate to Dedicated Manager"
                          >
                            <ArrowRight size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Slide-over Detailed Dossier Drawer */}
      {selectedRecord && (
        <UniversalDossierPanel
          record={selectedRecord}
          onClose={() => setSelectedRecord(null)}
          onNavigate={(basePath, id) => navigate(`/${basePath}/${id}`)}
        />
      )}
    </div>
  );
}
